// Recalibration v2 feature flags.
// Source: CogniPrint_Recalibration_Dev_Plan_v1.md §2.2.
//
// Effective value = these defaults, overridden per-environment by the
// `appConfig/featureFlags` Firestore doc (editable from the admin portal).
// Defaults below reflect what is GA-ready today: the cadence/Day-14/monthly
// surfaces ship on; the Coach add-on and its Phase-3 surfaces stay OFF until
// Lemon Squeezy billing is wired (see purchaseCoachSeats / REC-070/071).

// Note: despite the name, this is the app's runtime feature-flag registry (the
// recal flags were the first tenants). New non-recal flags (e.g. tutor.voice)
// live here too so they share the store/admin-page/server-enforcement plumbing.
export const RECALIBRATION_FLAGS = {
  CADENCE_V2: 'recal.cadenceV2',
  DAY14_PREVIEW: 'recal.day14Preview',
  DAY14_CHECK: 'recal.day14Check',
  MONTHLY_REPORT: 'recal.monthlyReport',
  SOFT_RESET: 'recal.softReset',
  PERSONALISATION: 'recal.personalisation',
  COACH: 'recal.coach',
  COUNTERFACTUAL: 'recal.counterfactual',
  AUTO_PILOT: 'recal.autoPilot',
  VOICE_TUTOR: 'tutor.voice',
} as const;

export type RecalibrationFlagKey =
  (typeof RECALIBRATION_FLAGS)[keyof typeof RECALIBRATION_FLAGS];

export const RECALIBRATION_FLAG_DEFAULTS: Record<RecalibrationFlagKey, boolean> = {
  [RECALIBRATION_FLAGS.CADENCE_V2]: true,
  [RECALIBRATION_FLAGS.DAY14_PREVIEW]: true,
  [RECALIBRATION_FLAGS.DAY14_CHECK]: true,
  [RECALIBRATION_FLAGS.MONTHLY_REPORT]: true,
  [RECALIBRATION_FLAGS.SOFT_RESET]: true,
  [RECALIBRATION_FLAGS.PERSONALISATION]: true,
  [RECALIBRATION_FLAGS.COACH]: false,
  [RECALIBRATION_FLAGS.COUNTERFACTUAL]: false,
  [RECALIBRATION_FLAGS.AUTO_PILOT]: false,
  [RECALIBRATION_FLAGS.VOICE_TUTOR]: false,
};

/** Ordered metadata for admin UIs and documentation. */
export const RECALIBRATION_FLAG_META: ReadonlyArray<{
  key: RecalibrationFlagKey;
  label: string;
  description: string;
}> = [
  { key: RECALIBRATION_FLAGS.CADENCE_V2, label: 'Cadence v2', description: 'Recalibration schedule, countdown, and full wizard.' },
  { key: RECALIBRATION_FLAGS.DAY14_PREVIEW, label: 'Day-14 Preview (Free)', description: 'Confidence Preview + conversion offer for Free users.' },
  { key: RECALIBRATION_FLAGS.DAY14_CHECK, label: 'Day-14 Check (Learner+)', description: 'Confidence Check retest for paid users.' },
  { key: RECALIBRATION_FLAGS.MONTHLY_REPORT, label: 'Monthly Report', description: 'Forward-looking monthly coaching report.' },
  { key: RECALIBRATION_FLAGS.SOFT_RESET, label: 'Soft Reset', description: 'Re-anchor cycle when 14+ days overdue.' },
  { key: RECALIBRATION_FLAGS.PERSONALISATION, label: 'Personalisation', description: 'Activity/profile/confidence cadence factors.' },
  { key: RECALIBRATION_FLAGS.COACH, label: 'Coach add-on', description: 'Coach seats, bi-weekly cadence. Requires Lemon Squeezy billing.' },
  { key: RECALIBRATION_FLAGS.COUNTERFACTUAL, label: 'Counterfactual simulator', description: 'Coach-only "what if" chip previews.' },
  { key: RECALIBRATION_FLAGS.AUTO_PILOT, label: 'Auto-pilot', description: 'Coach-only weekly auto-adaptation suggestions.' },
  { key: RECALIBRATION_FLAGS.VOICE_TUTOR, label: 'Voice Tutor', description: 'Hands-free voice conversation with Professor Cogni (Pro/Teams). §11.3.' },
];

/** Merge stored overrides over the defaults to get the effective flag map. */
export function resolveFeatureFlags(
  overrides?: Partial<Record<RecalibrationFlagKey, boolean>> | null,
): Record<RecalibrationFlagKey, boolean> {
  const resolved = { ...RECALIBRATION_FLAG_DEFAULTS };
  if (overrides) {
    for (const key of Object.keys(resolved) as RecalibrationFlagKey[]) {
      if (typeof overrides[key] === 'boolean') resolved[key] = overrides[key] as boolean;
    }
  }
  return resolved;
}

/** Firestore location of the override doc. */
export const FEATURE_FLAGS_DOC = { collection: 'appConfig', doc: 'featureFlags' } as const;
